import React from 'react';

/**
 * Truncate in the middle rather than at the end.
 *
 * CSS `text-overflow: ellipsis` can only cut the tail, which is the worst place
 * for a firmware URL — every entry shares the same host and folder, and the
 * filename that actually tells two builds apart is the first thing lost:
 *
 *   https://dontekcontrol.com/ota/Dontek_PSW_V1_1…     <- end ellipsis
 *   https://dontekcontrol.com/…/Dontek_PSW_V1_16_B03.bin  <- this
 *
 * Done with two spans instead of measuring text: the head shrinks and gets the
 * ellipsis, the tail is pinned. That keeps it responsive with no reflow work,
 * and it collapses to plain text when there's room.
 *
 * @param {Object} props
 * @param {string} [props.text]      full string to display
 * @param {string} [props.tail]      explicit tail; defaults to the last path segment
 * @param {string} [props.className]
 * @param {React.CSSProperties} [props.style]
 */
export const MiddleEllipsis = ({ text, tail, className, style }) => {
  const value = text == null ? '' : String(text);

  // Split at the last "/" so the tail is the filename. Anything without a slash
  // has nothing meaningful to pin, so it falls back to ordinary end-truncation.
  // An explicit tail wins, but only if it really is the end of the text —
  // otherwise the split would silently land in the wrong place.
  const useGivenTail = typeof tail === 'string' && tail.length > 0 && value.endsWith(tail);
  const splitAt = useGivenTail ? value.length - tail.length : value.lastIndexOf('/');
  const canSplit = splitAt > 0 && splitAt < value.length;

  return (
    <span
      className={className}
      title={value || undefined}
      style={{ display: 'flex', minWidth: 0, whiteSpace: 'nowrap', ...style }}
    >
      <span
        data-testid="middle-ellipsis-head"
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1 }}
      >
        {canSplit ? value.slice(0, splitAt) : value}
      </span>
      {canSplit && (
        // flexShrink: 0 so the head gives up space first and the filename survives.
        <span data-testid="middle-ellipsis-tail" style={{ flexShrink: 0 }}>
          {value.slice(splitAt)}
        </span>
      )}
    </span>
  );
};
